import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { IconButton } from '@atlaskit/button/new';
import Pressable from '@atlaskit/primitives/pressable';
import CrossIcon from '@atlaskit/icon/core/cross';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

import { usePageSpaceKey } from '@confluence/page-context';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { isPersonalSpace } from '@confluence/space-utils/entry-points/isPersonalSpace.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useFetch3PAuthentications } from './hooks/use3PAuthentication.ts';
import { ReviewContentSuggestionsModal } from './ReviewContentSuggestionsModal.tsx';
import { useFetchSuggestions } from './hooks/useFetchSuggestions.ts';
import { StackedContentTypeIcons } from './StackedContentTypeIcons.tsx';
import {
	ALL_EXTERNAL_PRODUCT_CONNECTIONS,
	type SuggestionProductKey,
	PRODUCT_ICON_MAP,
	DEFAULT_PRODUCT_INFO,
} from './__types__/RemoteObject.ts';
import { ThirdPartyAuthentication } from './ThirdPartyAuthentication.tsx';

const PRODUCT_LABELS: Record<SuggestionProductKey, string> = {
	googleDocs: 'Google Docs',
	googleSheets: 'Google Sheets',
	googleSlides: 'Google Slides',
	googleDrive: 'Google Drive',
	loom: 'Loom',
	figma: 'Figma',
	slack: 'Slack',
	link: 'Link',
};

export function getProductInfo(url: string): { product: string; iconUrl: string } {
	const productInfo =
		PRODUCT_ICON_MAP.find((entry) => url.includes(entry.pattern)) ?? DEFAULT_PRODUCT_INFO;
	return {
		product: PRODUCT_LABELS[productInfo.productKey],
		iconUrl: productInfo.iconUrl,
	};
}

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		marginTop: token('space.100'),
		overflow: 'hidden',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	mainAction: {
		flexGrow: 1,
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		// paddingBlock: token('space.100'),
		paddingInlineStart: token('space.100'),
		backgroundColor: 'transparent',
	},
	iconsWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	dismissButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: token('space.050'),
		backgroundColor: 'transparent',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed to inherit container hover
			backgroundColor: 'transparent',
		},
	},
});

const i18n = defineMessages({
	addContent: {
		id: 'side-navigation.space-navigation.related-content.add-content',
		defaultMessage: 'Add content from your apps',
		description: 'Label for the pill button that opens the content suggestions modal',
	},
	dismiss: {
		id: 'side-navigation.space-navigation.related-content.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the button to dismiss the related content panel',
	},
});

type SpaceNavigationRelatedContentProps = {
	spaceName?: string;
	spaceId?: string;
	iconPath?: string;
};

type SpaceNavigationRelatedContentBodyProps = {
	spaceKey: string;
	suggestionCount: number;
	iconUrls: string[];
	groups: ReturnType<typeof useFetchSuggestions>['groups'];
	refetch: ReturnType<typeof useFetchSuggestions>['refetch'];
	onDismiss: () => void;
	spaceName?: string;
	spaceId?: string;
	iconPath?: string;
};

const SpaceNavigationRelatedContentBody = ({
	spaceKey,
	suggestionCount,
	iconUrls,
	groups,
	refetch,
	onDismiss,
	spaceName,
	spaceId,
	iconPath,
}: SpaceNavigationRelatedContentBodyProps) => {
	const intl = useIntl();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const pageTreeStateUpdaterRef = useRef<PageTreeStateUpdater | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const mountValuesRef = useRef({ spaceId, suggestionCount, createAnalyticsEvent });

	useEffect(() => {
		const { spaceId: si, suggestionCount: sc, createAnalyticsEvent: cae } = mountValuesRef.current;
		cae({
			type: 'sendUIEvent',
			data: {
				action: 'rendered',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					entryPoint: 'suggestionsEntry',
					suggestionCount: sc,
				},
			},
		}).fire();
	}, []);

	const handleDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					entryPoint: 'suggestionsEntry',
					dismissMethod: 'close_button',
					suggestionCountAtDismiss: suggestionCount,
				},
			},
		}).fire();
		onDismiss();
	};

	return (
		<>
			<Box xcss={styles.container} testId="space-navigation-related-content">
				<Flex alignItems="center">
					<Pressable xcss={styles.mainAction} onClick={() => setIsModalOpen(true)}>
						<Box xcss={styles.iconsWrapper}>
							<StackedContentTypeIcons iconUrls={iconUrls} />
						</Box>
						<Text size="small" color="color.text.subtle" weight="medium">
							{intl.formatMessage(i18n.addContent)}
						</Text>
					</Pressable>
					<Box xcss={styles.dismissButton}>
						<IconButton
							icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
							appearance="subtle"
							spacing="compact"
							label={intl.formatMessage(i18n.dismiss)}
							onClick={handleDismiss}
							isTooltipDisabled={false}
						/>
					</Box>
				</Flex>
			</Box>
			<ModalTransition>
				{isModalOpen && (
					<ReviewContentSuggestionsModal
						spaceName={spaceName}
						iconPath={iconPath}
						groups={groups}
						iconUrls={iconUrls}
						spaceKey={spaceKey}
						spaceId={spaceId}
						onClose={() => setIsModalOpen(false)}
						onSaveSuccess={async () => {
							await refetch();
							setIsModalOpen(false);
							if (pageTreeStateUpdaterRef.current && groups[0]?.parentId) {
								pageTreeStateUpdaterRef.current.setForceFetchPageId(String(groups[0].parentId));
							}
						}}
					/>
				)}
			</ModalTransition>
			<Subscribe to={[PageTreeStateUpdater]}>
				{(pageTreeStateUpdater: PageTreeStateUpdater) => {
					pageTreeStateUpdaterRef.current = pageTreeStateUpdater;
					return null;
				}}
			</Subscribe>
		</>
	);
};

export const SpaceNavigationRelatedContent = ({
	spaceName,
	spaceId,
	iconPath,
}: SpaceNavigationRelatedContentProps) => {
	const [isVisible, setIsVisible] = useState(true);
	const [spaceKey] = usePageSpaceKey();
	const { groups, isLoading: isLoadingSuggestions, refetch } = useFetchSuggestions(spaceKey);
	const { cloudId } = useSessionData();
	const {
		unauthenticatedServices,
		isLoading: isLoadingThirdPartyAuth,
		refetch: refetchThirdPartyAuth,
	} = useFetch3PAuthentications({
		products: ALL_EXTERNAL_PRODUCT_CONNECTIONS,
		cloudId,
	});

	const suggestionCount = useMemo(
		() => groups.reduce((total, group) => total + group.remoteObjects.length, 0),
		[groups],
	);

	const iconUrls = useMemo(
		() =>
			Array.from(
				new Set(
					// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
					groups
						.flatMap((g) => g.remoteObjects)
						.map((s) => getProductInfo(s.url).iconUrl)
						.filter((url) => Boolean(url)),
				),
			),
		[groups],
	);

	if (
		!isLoadingSuggestions &&
		!isLoadingThirdPartyAuth &&
		groups.length === 0 &&
		Object.keys(unauthenticatedServices).length != 0
	) {
		return (
			<ThirdPartyAuthentication
				unauthenticatedServices={unauthenticatedServices}
				onThirdPartyAuthRefetch={refetchThirdPartyAuth}
				spaceName={spaceName}
				spaceId={spaceId}
				iconPath={iconPath}
			/>
		);
	}

	if (
		!spaceKey ||
		isPersonalSpace(spaceKey) ||
		!isVisible ||
		isLoadingSuggestions ||
		groups.length === 0
	) {
		return null;
	}

	return (
		<SpaceNavigationRelatedContentBody
			spaceKey={spaceKey}
			suggestionCount={suggestionCount}
			iconUrls={iconUrls}
			groups={groups}
			refetch={refetch}
			onDismiss={() => setIsVisible(false)}
			spaceName={spaceName}
			spaceId={spaceId}
			iconPath={iconPath}
		/>
	);
};
